/**
 * Reading an application whose candidate record no longer exists.
 *
 * An application stores `jobSeekerId` as a plain string and the server joins the candidate in with
 * a `$lookup`. A lookup that matches nothing yields an empty array rather than dropping the row, so
 * the application still comes back, with `jobSeeker` null. Every screen then read
 * `jobSeeker?.personalDetails?.fullName` and rendered an empty string, which is why 107 production
 * applications appeared as nameless rows that looked like a loading bug.
 *
 * They were not a loading bug. 21 job seekers were hard-deleted and their applications were left
 * behind. The names, emails and resumes are gone for good, because the identity lived only on the
 * deleted document, so the honest thing is to say so rather than render a blank. The record still
 * carries real value: the HR notes, the status history and which job it was for.
 *
 * The backend now refuses to hard-delete a candidate that still has applications
 * (ReferentialIntegrityException), so this should never grow. It has to keep working for the
 * records already in that state.
 */

/** Shown wherever a candidate name would otherwise be. */
export const DELETED_CANDIDATE_LABEL = 'Candidate record deleted';

interface ApplicationLike {
  jobSeekerId?: string | null;
  jobSeeker?: { personalDetails?: { fullName?: string | null } | null } | null;
}

/**
 * Whether this application points at a candidate who is no longer in the database.
 *
 * Requires `jobSeekerId` to be present: an application with no candidate id at all is a different
 * and much older kind of broken, and calling it "deleted" would be a guess. The check is on the
 * joined object rather than on the name, because a real candidate may legitimately have no name
 * recorded, and labelling them deleted would be a lie about a person who exists.
 */
export const isCandidateRecordDeleted = (app?: ApplicationLike | null): boolean =>
  Boolean(app?.jobSeekerId) && app?.jobSeeker == null;

/**
 * The name to display for an application's candidate.
 *
 * `fallback` is what an ordinary nameless-but-present candidate gets, which differs by screen: the
 * table wants an empty cell, the board and the workspace header want a word.
 */
export const candidateDisplayName = (app?: ApplicationLike | null, fallback = ''): string => {
  if (isCandidateRecordDeleted(app)) return DELETED_CANDIDATE_LABEL;
  return app?.jobSeeker?.personalDetails?.fullName || fallback;
};
